import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter, Observable } from 'rxjs';
import { PagedGrid, PageInfo, VisibleRangeModel } from '../../models';
import { PagedGridActions, PagedGridSelectors } from '../../store/paged-grid';

@Injectable({
  providedIn: 'root',
})
export class PagedGridFacade {
  private readonly store = inject(Store);

  init(params: {
    instanceId: string;
    pageSize: number;
    chunkSize: number;
    rowChunkSize: number;
    enableChunkCaching?: boolean;
  }): void {
    this.store.dispatch(
      PagedGridActions.Init({
        instanceId: params.instanceId,
        pageSize: params.pageSize,
        chunkSize: params.chunkSize,
        rowChunkSize: params.rowChunkSize,
        enableChunkCaching: params.enableChunkCaching
      }),
    );
  }

  reload(instanceId: string): void {
    this.store.dispatch(PagedGridActions.Reload({ instanceId }));
  }

  setDisplayedRange(instanceId: string, visibleRange: VisibleRangeModel): void {
    this.store.dispatch(PagedGridActions.SetDisplayedRange({ instanceId, visibleRange }));
  }

  selectPage(instanceId: string, pageIndex: number): void {
    this.store.dispatch(PagedGridActions.SelectPage({ instanceId, pageIndex }));
  }

  getPageData(instanceId: string): Observable<PageInfo> {
    return this.store.pipe(select(PagedGridSelectors.selectPageInfo(instanceId)));
  }

  getGrid(instanceId: string): Observable<PagedGrid.ComputedGridModel> {
    return this.store.pipe(
      select(PagedGridSelectors.selectGridView(instanceId)),
      filter((data) => Boolean(data)),
    );
  }

  getIsGridInitializedWithData(instanceId: string): Observable<boolean> {
    return this.store.pipe(
      select(PagedGridSelectors.selectAnyChunkLoaded(instanceId)),
    );
  }

  getSortInfo(instanceId: string): Observable<{ sortColumn: string; sortDirection: 'asc' | 'desc' }> {
    return this.store.pipe(
      select(PagedGridSelectors.selectSortInfo(instanceId)),
      filter((data) => Boolean(data)),
    );
  }

  showMoreInRow(instanceId: string, rowIndex: number, chunkIndex: number): void {
    this.store.dispatch(PagedGridActions.ShowMoreInRow({ instanceId, rowIndex, chunkIndex }));
  }

  showLessInRow(instanceId: string, rowIndex: number, chunkIndex: number): void {
    this.store.dispatch(PagedGridActions.ShowLessInRow({ instanceId, rowIndex, chunkIndex }));
  }

  showAllInRow(instanceId: string, rowIndex: number, rowIndexInChunk: number, chunkIndex: number): void {
    this.store.dispatch(PagedGridActions.ExpandRow({ rowIndexInChunk, instanceId, rowIndex, chunkIndex }));
  }

  hideAllInRow(instanceId: string, rowIndex: number, rowIndexInChunk: number, chunkIndex: number): void {
    this.store.dispatch(PagedGridActions.CollapseRow({ rowIndexInChunk, instanceId, rowIndex, chunkIndex }));
  }

  changeSortColumn(instanceId: string, columnName: string): void {
    this.store.dispatch(PagedGridActions.ChangeSortColumn({ instanceId, columnName }));
  }

  trackSortingColumnEvent(columnName: string, sortDirection: string): void {
    this.store.dispatch(PagedGridActions.TrackSortColumnEvent({ columnName, sortDirection }));
  }
}
